import React, { useMemo, useState } from 'react';
import {
  ScatterChart as RechartsScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ZAxis,
} from 'recharts';
import { ChartContainer, ChartConfig } from './ChartContainer';
import { ChartWrapper } from './ChartWrapper';
import { ChartTooltipContent } from './ChartTooltip';
import { ChartLegendContent } from './ChartLegend';
import { defaultPalette } from '../../lib/chartPalette';
import { truncateLabel, calculateOptimalLabelLength } from '../../lib/chartLabelUtils';
import { sanitizeForCSSVar } from '../../lib/chartCssUtils';

export interface ScatterChartProps {
  data: any[];
  config?: ChartConfig;
  title?: string;
  description?: string;
  xAxisKey: string;
  yAxisKey: string;
  scatters: {
    name: string;
    data: any[];
    fill?: string;
    shape?: 'circle' | 'cross' | 'diamond' | 'square' | 'star' | 'triangle' | 'wye';
  }[];
  showGrid?: boolean;
  showTooltip?: boolean;
  showLegend?: boolean;
  height?: number;
  className?: string;
  tooltipFormatter?: (value: any) => string;
  xAxisFormatter?: (value: any) => string;
  yAxisFormatter?: (value: any) => string;
  xAxisLabel?: string;
  yAxisLabel?: string;
  /** Enable expand button to view chart in fullscreen dialog */
  expandable?: boolean;
  /** Custom height for the expanded chart in the dialog */
  expandedHeight?: number;
  /** @internal Set by ChartWrapper to indicate expanded view - shows all ticks with longer labels */
  isExpanded?: boolean;
}

// Helper to auto-generate config from scatters
const useChartDefaults = (
  scatters: ScatterChartProps['scatters'],
  providedConfig?: ChartConfig
) => {
  return useMemo(() => {
    if (!scatters || scatters.length === 0) {
      return providedConfig || {};
    }

    // Use provided config or auto-generate with default (violet) palette colors
    const config = providedConfig || scatters.reduce((acc, scatter, index) => {
      acc[scatter.name] = {
        label: scatter.name.charAt(0).toUpperCase() + scatter.name.slice(1), // Capitalize first letter
        color: defaultPalette[index % defaultPalette.length],
      };
      return acc;
    }, {} as ChartConfig);

    return config;
  }, [scatters, providedConfig]);
};

export const ScatterChart: React.FC<ScatterChartProps> = ({
  data: _data,
  config: providedConfig,
  title,
  description,
  xAxisKey,
  yAxisKey,
  scatters,
  showGrid = true,
  showTooltip = true,
  showLegend = true,
  height = 350,
  className,
  tooltipFormatter,
  xAxisFormatter,
  yAxisFormatter,
  xAxisLabel,
  yAxisLabel,
  expandable = true,
  expandedHeight = 500,
  isExpanded = false,
}) => {
  const config = useChartDefaults(scatters, providedConfig);
  const [hoveredDataKey, setHoveredDataKey] = useState<string | null>(null);

  // Add extra bottom margin to ensure legend doesn't overlap with axis labels
  const chartMargin = useMemo(() => {
    const baseLegendSpacing = 35;
    const expandedExtraSpace = isExpanded ? 20 : 0; // Extra space in expanded view
    return { top: 5, right: 10, left: 10, bottom: 5 + baseLegendSpacing + expandedExtraSpace };
  }, [isExpanded]);

  // Calculate total data points across all scatters for optimal label length
  const totalDataPoints = useMemo(() => {
    return scatters.reduce((total, scatter) => total + (scatter.data?.length || 0), 0);
  }, [scatters]);

  // Create x-axis formatter that truncates labels and applies custom formatting
  // In expanded view: 20 chars for better readability, otherwise use responsive calculation
  const xAxisFormatterWithTruncation = useMemo(() => {
    const maxLength = isExpanded ? 20 : calculateOptimalLabelLength(totalDataPoints);
    return (value: any) => {
      // Apply custom formatter first if provided
      const formattedValue = xAxisFormatter ? xAxisFormatter(value) : value;
      // Then truncate to prevent overlap
      return truncateLabel(formattedValue, maxLength);
    };
  }, [totalDataPoints, xAxisFormatter, isExpanded]);

  const chartContent = (
    <ChartContainer config={config}>
      <ResponsiveContainer width="100%" height={height}>
        <RechartsScatterChart margin={chartMargin}>
          {showGrid && (
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="hsl(var(--border))"
            />
          )}
          <XAxis
            type="number"
            dataKey={xAxisKey}
            name={xAxisLabel || xAxisKey}
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tickFormatter={xAxisFormatterWithTruncation}
            className="text-xs"
          />
          <YAxis
            type="number"
            dataKey={yAxisKey}
            name={yAxisLabel || yAxisKey}
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tickFormatter={yAxisFormatter}
            className="text-xs"
          />
          <ZAxis range={[60, 400]} />
          {showTooltip && (
            <Tooltip
              content={
                <ChartTooltipContent
                  valueFormatter={tooltipFormatter}
                  indicator="dot"
                />
              }
              cursor={{ strokeDasharray: '3 3' }}
            />
          )}
          {showLegend && (
            <Legend
              content={
                <ChartLegendContent
                  onMouseEnter={(dataKey) => setHoveredDataKey(dataKey)}
                  onMouseLeave={() => setHoveredDataKey(null)}
                />
              }
            />
          )}
          {scatters.map((scatter, _index) => (
            <Scatter
              key={scatter.name}
              name={scatter.name}
              data={scatter.data}
              fill={
                scatter.fill ||
                config[scatter.name]?.color ||
                `var(--color-${sanitizeForCSSVar(scatter.name)})`
              }
              shape={scatter.shape || 'circle'}
              opacity={hoveredDataKey === null || hoveredDataKey === scatter.name ? 1 : 0.3}
            />
          ))}
        </RechartsScatterChart>
      </ResponsiveContainer>
    </ChartContainer>
  );

  return (
    <ChartWrapper
      title={title}
      description={description}
      className={className}
      expandable={expandable}
      expandedHeight={expandedHeight}
      chartComponent={ScatterChart}
      chartProps={{
        data: _data,
        config: providedConfig,
        xAxisKey,
        yAxisKey,
        scatters,
        showGrid,
        showTooltip,
        showLegend,
        height,
        tooltipFormatter,
        xAxisFormatter,
        yAxisFormatter,
        xAxisLabel,
        yAxisLabel,
        // isExpanded will be added by ChartWrapper in expanded view
      }}
    >
      {chartContent}
    </ChartWrapper>
  );
};
